import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { ScrollToTop } from '../../../components/common/scroll-to-top/scroll-to-top';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProfileSidebar } from '../../../layout/profile-sidebar/profile-sidebar';
import { ActivatedRoute, Router } from '@angular/router';
import { Posts } from '../../posts/posts';
import { Events } from '../../events/events';
import { Blog } from '../../blog/blog';
import { UserReactions } from '../../user-reactions/user-reactions';
import { FollowersFollowing } from '../../followers-following/followers-following';
import { FriendsList } from '../../friends-list/friends-list';
import { BlockedUsers } from '../../blocked-users/blocked-users';
import { AuthStateService } from '../../../shared/states/auth.state';

type ProfileTabType = 'post' | 'event' | 'blog' | 'reaction' | 'follower' | 'following' | 'friend' | 'blocked';

interface ProfileTab {
  label: string;
  type: ProfileTabType | null;
}

@Component({
  selector: 'app-profile',
  imports: [CommonModule, ScrollToTop, ProfileSidebar, Posts, Events, Blog, UserReactions, FollowersFollowing, FriendsList, BlockedUsers],
  templateUrl: './profile.html'
})
export class Profile implements OnInit, OnDestroy {
  private authState = inject(AuthStateService);
  private platformId = inject(PLATFORM_ID);
  private readonly destroy$ = new Subject<void>();

  @ViewChild('tabBar') tabBarRef?: ElementRef<HTMLElement>;

  private readonly baseTabs: ProfileTab[] = [
    { label: 'Posts',       type: 'post'      },
    { label: 'Blogs',       type: 'blog'      },
    { label: 'Events',      type: 'event'     },
    { label: 'Reactions',   type: 'reaction'  },
    // { label: 'Collections', type: null        },
    { label: 'Followers',   type: 'follower'  },
    { label: 'Following',   type: 'following' },
    { label: 'Friends',     type: 'friend'    },
  ];

  private static readonly BLOCKED_TAB: ProfileTab = { label: 'Blocked', type: 'blocked' };

  /**
   * "Blocked" only ever appears on your own profile — nobody else can see or
   * reach who you've blocked (the API itself is scoped to the logged-in user
   * regardless, this just keeps the tab out of the UI for visitors).
   *
   * Held as a plain field, rebuilt only when it can actually change. It used to
   * be a getter that returned `[...baseTabs, { label: 'Blocked', ... }]`, which
   * handed *ngFor a brand-new array — and a brand-new Blocked object — on every
   * single change-detection pass, so the tab bar was being torn down and
   * rebuilt continuously. Only on your own profile, because visitors got the
   * stable `baseTabs` reference back instead.
   */
  tabs: ProfileTab[] = this.baseTabs;

  private refreshTabs(): void {
    this.tabs = this.isOwnProfile
      ? [...this.baseTabs, Profile.BLOCKED_TAB]
      : this.baseTabs;
  }

  /** Identity for *ngFor, so the buttons survive any future array rebuild. */
  trackByTabLabel(_: number, tab: ProfileTab): string { return tab.label; }

  activeTab  = 'Posts';
  activeType: ProfileTabType = 'post';
  userName   = '';

  /** Right-side toggle on the Posts/Events/Blogs tabs — when on, only shows
   *  items where this profile's user is an accepted collaborator rather than
   *  the author/host. Resets whenever the tab changes since it only applies
   *  to content tabs. */
  collabOnly = false;

  /** Only Posts, Events and Blogs carry a collaboration concept — the toggle
   *  is hidden on every other tab. */
  get showCollabToggle(): boolean {
    return this.activeType === 'post' || this.activeType === 'event' || this.activeType === 'blog';
  }

  toggleCollabOnly(): void {
    this.collabOnly = !this.collabOnly;
  }

  get isOwnProfile(): boolean {
    const auth = this.authState.getAuthSnapshot();
    return !!(auth?.isLoggedIn && auth?.user?.userName && auth.user.userName === this.userName);
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.userName = params.get('userName') ?? '';
      const rawType = (params.get('type') ?? 'post').toLowerCase();
      this.collabOnly = false;

      if      (rawType.startsWith('event'))     this.activeType = 'event';
      else if (rawType.startsWith('blog'))      this.activeType = 'blog';
      else if (rawType.startsWith('reaction'))  this.activeType = 'reaction';
      else if (rawType.startsWith('follower'))  this.activeType = 'follower';
      else if (rawType.startsWith('following')) this.activeType = 'following';
      else if (rawType.startsWith('friend'))    this.activeType = 'friend';
      else if (rawType.startsWith('blocked'))   this.activeType = 'blocked';
      else                                      this.activeType = 'post';

      // Nobody but the profile owner may view the blocked-users tab — bounce
      // anyone else straight back to Posts (covers direct URL navigation;
      // the tab button itself is already hidden for visitors).
      if (this.activeType === 'blocked' && !this.isOwnProfile) {
        this.router.navigate(['/profile', this.userName, 'post']);
        return;
      }

      const typeToLabel: Record<string, string> = {
        post: 'Posts', blog: 'Blogs', event: 'Events', reaction: 'Reactions',
        follower: 'Followers', following: 'Following', friend: 'Friends', blocked: 'Blocked'
      };
      this.activeTab = typeToLabel[this.activeType] ?? 'Posts';
      this.refreshTabs();
    });

    // Auth is seeded synchronously from storage, but log in/out can still flip
    // ownership while this page is mounted.
    this.authState.auth$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshTabs());
  }

  ngOnDestroy(): void {
    // Retires any in-flight scroll loop along with the component.
    this.scrollRun++;
    this.destroy$.next();
    this.destroy$.complete();
  }

  setTab(tab: ProfileTab): void {
    console.log('setTab', tab);
    this.activeTab = tab.label;
    this.collabOnly = false;
    if (!tab.type || !this.userName) return;
    if (tab.type === this.activeType) return;
    this.router
      .navigate(['/profile', this.userName, tab.type])
      .then(() => this.scrollTabBarToTop());
  }

  /**
   * How long to keep re-aiming while the new tab's content loads.
   *
   * Generous on purpose. The window has to outlast a real network fetch on a
   * phone, not a warm localhost round-trip — 800ms was enough in desktop
   * responsive mode and expired before the list arrived on an actual device,
   * which is why this worked there and only intermittently on a phone. The loop
   * exits the moment it converges, so a fast tab costs a frame or two.
   */
  private static readonly SCROLL_SETTLE_MS = 3000;
  /** Frames the target must hold before we accept it and stop re-aiming. */
  private static readonly SCROLL_STABLE_FRAMES = 3;

  /**
   * Switching tabs is a router navigation, and the app is configured with
   * `scrollPositionRestoration: 'enabled'` (app.config.ts), so the router jumps
   * the window to y=0. On mobile the profile sidebar stacks above the tab bar,
   * so that lands the user on the sidebar with the tabs pushed off-screen. Park
   * the tab bar just under the sticky header instead, so the newly selected
   * content starts right below the tabs.
   *
   * Re-aimed every frame for a short window rather than set once: at the moment
   * the tab changes the old list has been destroyed and the new one hasn't
   * loaded, so the document is often too short to scroll that far and the
   * browser clamps us part-way. The page then grows as items arrive. Each pass
   * measures again and closes the remaining gap, and we stop as soon as it's
   * closed — or when the user takes over.
   */
  /** Bumped per run so a rapid second tab tap retires the previous loop. */
  private scrollRun = 0;

  private scrollTabBarToTop(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const run = ++this.scrollRun;
    const deadline = Date.now() + Profile.SCROLL_SETTLE_MS;
    let cancelled = false;
    let stableFrames = 0;
    const cancel = () => { cancelled = true; };
    // Never fight the user for the scroll position — but only a real drag or
    // wheel counts as taking over. This used to listen for `touchstart`, which
    // on a phone also fires from the very tap that switched the tab (and from a
    // finger resting on the screen), cancelling the scroll before it began.
    // That is the main reason this behaved on desktop and not on a device:
    // a mouse click never produces a touch event at all.
    window.addEventListener('touchmove', cancel, { passive: true });
    window.addEventListener('wheel', cancel, { passive: true });

    const cleanup = () => {
      window.removeEventListener('touchmove', cancel);
      window.removeEventListener('wheel', cancel);
    };

    const step = () => {
      const tabBar = this.tabBarRef?.nativeElement;
      // `run !== this.scrollRun` — a newer tab tap has taken over.
      if (cancelled || !tabBar || run !== this.scrollRun || Date.now() > deadline) { cleanup(); return; }

      const header = document.querySelector('header');
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const delta = tabBar.getBoundingClientRect().top - headerHeight;

      if (Math.abs(delta) <= 1) {
        // Hold the position for a few frames before trusting it: on iOS the
        // address bar collapsing (and late-arriving content) shifts the page
        // right after we look correct.
        if (++stableFrames >= Profile.SCROLL_STABLE_FRAMES) { cleanup(); return; }
      } else {
        stableFrames = 0;
        // Instant, not smooth: smooth scrolling would be restarted by every pass.
        window.scrollBy({ top: delta, behavior: 'auto' });
      }
      requestAnimationFrame(step);
    };

    // The router scrolls on NavigationEnd, in the same tick this promise
    // resolves — defer so we run after it rather than racing it.
    setTimeout(() => requestAnimationFrame(step));
  }
}